import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  FileText, CheckSquare, Calendar as CalendarIcon, Plus, Trash2, Edit2, Check, X, 
  Sparkles, Copy, Search, ArrowLeft, Clock, Users, Brain, UserPlus, Settings, Save,
  Code, Wallet, Gamepad2, Heart, Lock, Mic, MicOff, Loader2, Pin, Tag, Bell, BellOff,
  MapPin, Repeat, AlertCircle, Flag, HelpCircle, Lightbulb, Send, MessageSquare, Eye,
  GraduationCap, Award
} from 'lucide-react';

export default function Tools({ navigate }) {
  const { user, profile } = useAuth();
  const [activeTool, setActiveTool] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [friendGroups, setFriendGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [aiPreferences, setAiPreferences] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [promptSearch, setPromptSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Enhanced form states
  const [noteForm, setNoteForm] = useState({ 
    title: '', 
    content: '', 
    color: '#00d4ff',
    pinned: false,
    tags: ''
  });
  const [taskForm, setTaskForm] = useState({ 
    title: '', 
    description: '',
    due_date: '', 
    due_time: '',
    priority: 'medium',
    category: 'personal',
    reminder: false
  });
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    color: '#00d4ff',
    recurring: 'none',
    reminder: '15'
  });
  const [promptForm, setPromptForm] = useState({ title: '', content: '', category: 'general' });
  const [groupForm, setGroupForm] = useState({ name: '', type: 'group', description: '' });
  const [supportForm, setSupportForm] = useState({ subject: '', description: '', priority: 'medium' });
  const [featureForm, setFeatureForm] = useState({ title: '', description: '', category: 'feature' });
  const [submitSuccess, setSubmitSuccess] = useState(null);
  
  // Voice dictation state
  const [isRecordingNote, setIsRecordingNote] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Categories and options
  const noteColors = [
    { id: '#00d4ff', name: 'Blue' },
    { id: '#00ff88', name: 'Green' },
    { id: '#ff88ff', name: 'Pink' },
    { id: '#ffcc00', name: 'Yellow' },
    { id: '#ff6644', name: 'Orange' }
  ];
  
  const taskPriorities = [
    { id: 'high', name: 'High', color: '#ff3366' },
    { id: 'medium', name: 'Medium', color: '#ffcc00' },
    { id: 'low', name: 'Low', color: '#00ff88' }
  ];
  
  const taskCategories = [
    { id: 'personal', name: 'Personal' },
    { id: 'work', name: 'Work' },
    { id: 'health', name: 'Health' },
    { id: 'finance', name: 'Finance' },
    { id: 'family', name: 'Family' }
  ];
  
  const recurringOptions = [
    { id: 'none', name: 'Does not repeat' },
    { id: 'daily', name: 'Daily' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'yearly', name: 'Yearly' }
  ];
  
  const reminderOptions = [
    { id: '0', name: 'At time of event' },
    { id: '5', name: '5 minutes before' },
    { id: '15', name: '15 minutes before' },
    { id: '30', name: '30 minutes before' },
    { id: '60', name: '1 hour before' },
    { id: '1440', name: '1 day before' }
  ];
  
  const [aiForm, setAiForm] = useState({
    user_name: '',
    ai_nickname: 'Sage',
    about_me: '',
    communication_style: 'friendly',
    interests: '',
    work_context: '',
    custom_instructions: ''
  });

  const promptCategories = [
    { id: 'general', name: 'General', color: '#00d4ff' },
    { id: 'coding', name: 'Coding', color: '#00ff88' },
    { id: 'writing', name: 'Writing', color: '#ff88ff' },
    { id: 'business', name: 'Business', color: '#ffcc00' },
    { id: 'creative', name: 'Creative', color: '#ff6644' }
  ];

  const groupTypes = [
    { id: 'group', name: 'Group', color: '#00d4ff' },
    { id: 'family', name: 'Family', color: '#ff88ff' },
    { id: 'team', name: 'Team', color: '#00ff88' },
    { id: 'work', name: 'Work', color: '#ffcc00' }
  ];

  const communicationStyles = [
    { id: 'friendly', name: 'Friendly & Casual' },
    { id: 'professional', name: 'Professional' },
    { id: 'concise', name: 'Concise & Direct' },
    { id: 'detailed', name: 'Detailed & Thorough' },
    { id: 'creative', name: 'Creative & Playful' }
  ];

  const tools = [
    { id: 'notes', label: 'Notes', icon: FileText, description: 'Create and manage notes', count: notes.length },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, description: 'Track your to-dos', count: tasks.filter(t => !t.completed).length },
    { id: 'prompts', label: 'Prompt Vault', icon: Sparkles, description: 'Save AI prompts', count: prompts.length },
    { id: 'calendar', label: 'Calendar', icon: CalendarIcon, description: 'View your schedule', count: events.length },
    { id: 'friends', label: 'Friends', icon: Users, description: 'Groups, families & teams', count: friendGroups.length },
    { id: 'train-ai', label: 'Train My AI', icon: Brain, description: 'Personalize your assistant', count: null },
    { id: 'support', label: 'Support', icon: HelpCircle, description: 'Report issues', count: null },
    { id: 'feature-ideas', label: 'Feature Ideas', icon: Lightbulb, description: 'Suggest improvements', count: null },
    { id: 'my-submissions', label: 'My Submissions', icon: MessageSquare, description: 'Track your requests', count: submissions.filter(s => s.status !== 'completed').length }
  ];

  useEffect(() => {
    fetchNotes();
    fetchTasks();
    fetchPrompts();
    fetchFriendGroups();
    fetchAiPreferences();
    fetchEvents();
    fetchSubmissions();
  }, []);

  useEffect(() => {
    if (activeTool === 'notes') fetchNotes();
    else if (activeTool === 'tasks') fetchTasks();
    else if (activeTool === 'prompts') fetchPrompts();
    else if (activeTool === 'friends') fetchFriendGroups();
    else if (activeTool === 'train-ai') fetchAiPreferences();
    else if (activeTool === 'calendar') fetchEvents();
    else if (activeTool === 'my-submissions') fetchSubmissions();
  }, [activeTool]);

  // Friend Groups Functions
  const fetchFriendGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('friend_groups')
        .select('*, friend_group_members(user_id)')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Fallback to localStorage
        const localGroups = JSON.parse(localStorage.getItem(`sage_friend_groups_${user.id}`) || '[]');
        setFriendGroups(localGroups);
      } else {
        setFriendGroups(data || []);
      }
    } catch (error) {
      const localGroups = JSON.parse(localStorage.getItem(`sage_friend_groups_${user.id}`) || '[]');
      setFriendGroups(localGroups);
    }
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name.trim()) return;

    const groupData = {
      owner_id: user.id,
      name: groupForm.name.trim(),
      type: groupForm.type,
      description: groupForm.description.trim(),
      created_at: new Date().toISOString()
    };

    try {
      if (editingGroup) {
        const { error } = await supabase.from('friend_groups').update(groupData).eq('id', editingGroup.id);
        if (error) throw error;
        setFriendGroups(prev => prev.map(g => g.id === editingGroup.id ? { ...g, ...groupData } : g));
      } else {
        const { data, error } = await supabase.from('friend_groups').insert(groupData).select().single();
        if (error) throw error;
        setFriendGroups(prev => [data, ...prev]);
      }
    } catch (error) {
      // Fallback to localStorage
      if (editingGroup) {
        setFriendGroups(prev => prev.map(g => g.id === editingGroup.id ? { ...g, ...groupData } : g));
      } else {
        const newGroup = { ...groupData, id: `local_${Date.now()}`, friend_group_members: [] };
        setFriendGroups(prev => [newGroup, ...prev]);
      }
      const updatedGroups = editingGroup 
        ? friendGroups.map(g => g.id === editingGroup.id ? { ...g, ...groupData } : g)
        : [{ ...groupData, id: `local_${Date.now()}` }, ...friendGroups];
      localStorage.setItem(`sage_friend_groups_${user.id}`, JSON.stringify(updatedGroups));
    }

    setGroupForm({ name: '', type: 'group', description: '' });
    setEditingGroup(null);
    setShowForm(false);
  };

  const handleDeleteGroup = async (id) => {
    try {
      await supabase.from('friend_groups').delete().eq('id', id);
    } catch (error) {}
    setFriendGroups(prev => prev.filter(g => g.id !== id));
    localStorage.setItem(`sage_friend_groups_${user.id}`, JSON.stringify(friendGroups.filter(g => g.id !== id)));
  };

  // AI Preferences Functions
  const fetchAiPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('user_ai_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        // Try localStorage or set defaults
        const localPrefs = JSON.parse(localStorage.getItem(`sage_ai_prefs_${user.id}`) || 'null');
        if (localPrefs) {
          setAiPreferences(localPrefs);
          setAiForm(localPrefs);
        } else {
          // Set defaults from profile
          const defaults = {
            user_name: profile?.username || '',
            ai_nickname: 'Sage',
            about_me: '',
            communication_style: 'friendly',
            interests: '',
            work_context: '',
            custom_instructions: ''
          };
          setAiPreferences(defaults);
          setAiForm(defaults);
        }
      } else {
        setAiPreferences(data);
        setAiForm(data);
      }
    } catch (error) {
      const localPrefs = JSON.parse(localStorage.getItem(`sage_ai_prefs_${user.id}`) || 'null');
      if (localPrefs) {
        setAiPreferences(localPrefs);
        setAiForm(localPrefs);
      }
    }
  };

  const handleSaveAiPreferences = async (e) => {
    e.preventDefault();
    setLoading(true);

    const prefsData = {
      user_id: user.id,
      user_name: aiForm.user_name.trim(),
      ai_nickname: aiForm.ai_nickname.trim() || 'Sage',
      about_me: aiForm.about_me.trim(),
      communication_style: aiForm.communication_style,
      interests: aiForm.interests.trim(),
      work_context: aiForm.work_context.trim(),
      custom_instructions: aiForm.custom_instructions.trim(),
      updated_at: new Date().toISOString()
    };

    try {
      // Try upsert
      const { error } = await supabase
        .from('user_ai_preferences')
        .upsert(prefsData, { onConflict: 'user_id' });

      if (error) throw error;
      setAiPreferences(prefsData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      // Fallback to localStorage
      localStorage.setItem(`sage_ai_prefs_${user.id}`, JSON.stringify(prefsData));
      setAiPreferences(prefsData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  // Calendar Events Functions
  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: true });

      if (error) {
        // Fallback to localStorage
        const localEvents = JSON.parse(localStorage.getItem(`sage_events_${user.id}`) || '[]');
        setEvents(localEvents);
      } else {
        setEvents(data || []);
      }
    } catch (error) {
      const localEvents = JSON.parse(localStorage.getItem(`sage_events_${user.id}`) || '[]');
      setEvents(localEvents);
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.date) return;

    const eventData = {
      user_id: user.id,
      title: eventForm.title.trim(),
      description: eventForm.description.trim(),
      date: eventForm.date,
      start_time: eventForm.start_time || null,
      end_time: eventForm.end_time || null,
      location: eventForm.location.trim(),
      color: eventForm.color,
      recurring: eventForm.recurring,
      reminder: eventForm.reminder,
      created_at: new Date().toISOString()
    };

    try {
      if (editingEvent) {
        const { error } = await supabase.from('calendar_events').update(eventData).eq('id', editingEvent.id);
        if (error) throw error;
        setEvents(prev => prev.map(e => e.id === editingEvent.id ? { ...e, ...eventData } : e));
      } else {
        const { data, error } = await supabase.from('calendar_events').insert(eventData).select().single();
        if (error) throw error;
        setEvents(prev => [data, ...prev].sort((a, b) => new Date(a.date) - new Date(b.date)));
      }
    } catch (error) {
      // Fallback to localStorage
      if (editingEvent) {
        setEvents(prev => prev.map(e => e.id === editingEvent.id ? { ...e, ...eventData } : e));
      } else {
        const newEvent = { ...eventData, id: `local_${Date.now()}` };
        setEvents(prev => [...prev, newEvent].sort((a, b) => new Date(a.date) - new Date(b.date)));
      }
      // Save to localStorage
      const updatedEvents = editingEvent 
        ? events.map(e => e.id === editingEvent.id ? { ...e, ...eventData } : e)
        : [...events, { ...eventData, id: `local_${Date.now()}` }];
      localStorage.setItem(`sage_events_${user.id}`, JSON.stringify(updatedEvents));
    }

    setEventForm({ title: '', description: '', date: '', start_time: '', end_time: '', location: '', color: '#00d4ff', recurring: 'none', reminder: '15' });
    setEditingEvent(null);
    setShowForm(false);
    setSelectedDay(null);
  };

  const handleDeleteEvent = async (id) => {
    try {
      await supabase.from('calendar_events').delete().eq('id', id);
    } catch (error) {}
    setEvents(prev => prev.filter(e => e.id !== id));
    localStorage.setItem(`sage_events_${user.id}`, JSON.stringify(events.filter(e => e.id !== id)));
  };

  const getEventsForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  // Support & Feature Submissions Functions
  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        const localSubmissions = JSON.parse(localStorage.getItem(`sage_submissions_${user.id}`) || '[]');
        setSubmissions(localSubmissions);
      } else {
        setSubmissions(data || []);
      }
    } catch (error) {
      const localSubmissions = JSON.parse(localStorage.getItem(`sage_submissions_${user.id}`) || '[]');
      setSubmissions(localSubmissions);
    }
  };

  const handleSubmitSupport = async (e) => {
    e.preventDefault();
    if (!supportForm.subject.trim() || !supportForm.description.trim()) return;

    const submissionData = {
      user_id: user.id,
      username: profile?.username || 'User',
      type: 'support',
      subject: supportForm.subject.trim(),
      description: supportForm.description.trim(),
      priority: supportForm.priority,
      status: 'pending',
      created_at: new Date().toISOString()
    };

    try {
      const { data, error } = await supabase.from('user_submissions').insert(submissionData).select().single();
      if (error) throw error;
      setSubmissions(prev => [data, ...prev]);
    } catch (error) {
      // Fallback to localStorage
      const newSubmission = { ...submissionData, id: `local_${Date.now()}` };
      setSubmissions(prev => [newSubmission, ...prev]);
      const allSubmissions = [newSubmission, ...submissions];
      localStorage.setItem(`sage_submissions_${user.id}`, JSON.stringify(allSubmissions));
    }

    setSupportForm({ subject: '', description: '', priority: 'medium' });
    setSubmitSuccess('support');
    setTimeout(() => setSubmitSuccess(null), 2000);
    setShowForm(false);
  };

  const handleSubmitFeature = async (e) => {
    e.preventDefault();
    if (!featureForm.title.trim() || !featureForm.description.trim()) return;

    const submissionData = {
      user_id: user.id,
      username: profile?.username || 'User',
      type: 'feature',
      subject: featureForm.title.trim(),
      description: featureForm.description.trim(),
      priority: featureForm.category,
      status: 'pending',
      created_at: new Date().toISOString()
    };

    try {
      const { data, error } = await supabase.from('user_submissions').insert(submissionData).select().single();
      if (error) throw error;
      setSubmissions(prev => [data, ...prev]);
    } catch (error) {
      // Fallback to localStorage
      const newSubmission = { ...submissionData, id: `local_${Date.now()}` };
      setSubmissions(prev => [newSubmission, ...prev]);
      const allSubmissions = [newSubmission, ...submissions];
      localStorage.setItem(`sage_submissions_${user.id}`, JSON.stringify(allSubmissions));
    }

    setFeatureForm({ title: '', description: '', category: 'feature' });
    setSubmitSuccess('feature');
    setTimeout(() => setSubmitSuccess(null), 2000);
    setShowForm(false);
  };

  const supportPriorities = [
    { id: 'low', name: 'Low', color: '#00ff88' },
    { id: 'medium', name: 'Medium', color: '#ffcc00' },
    { id: 'high', name: 'High', color: '#ff3366' }
  ];

  const featureCategories = [
    { id: 'feature', name: 'New Feature', color: '#00d4ff' },
    { id: 'ui', name: 'UI/Design', color: '#ff88ff' },
    { id: 'integration', name: 'Integration', color: '#00ff88' },
    { id: 'other', name: 'Other', color: '#ffcc00' }
  ];

  const submissionStatuses = {
    pending: { label: 'Pending', color: '#ffcc00' },
    'in-progress': { label: 'In Progress', color: '#00d4ff' },
    completed: { label: 'Completed', color: '#00ff88' },
    declined: { label: 'Declined', color: '#ff3366' }
  };

  // Prompts Functions
  const fetchPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from('prompt_vault')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        const localPrompts = JSON.parse(localStorage.getItem(`sage_prompts_${user.id}`) || '[]');
        setPrompts(localPrompts);
      } else {
        setPrompts(data || []);
      }
    } catch (error) {
      const localPrompts = JSON.parse(localStorage.getItem(`sage_prompts_${user.id}`) || '[]');
      setPrompts(localPrompts);
    }
  };

  const handleSavePrompt = async (e) => {
    e.preventDefault();
    if (!promptForm.title.trim() || !promptForm.content.trim()) return;

    const promptData = {
      user_id: user.id,
      title: promptForm.title.trim(),
      content: promptForm.content.trim(),
      category: promptForm.category,
      created_at: new Date().toISOString()
    };

    try {
      if (editingPrompt) {
        await supabase.from('prompt_vault').update(promptData).eq('id', editingPrompt.id);
        setPrompts(prev => prev.map(p => p.id === editingPrompt.id ? { ...p, ...promptData } : p));
      } else {
        const { data } = await supabase.from('prompt_vault').insert(promptData).select().single();
        if (data) setPrompts(prev => [data, ...prev]);
      }
    } catch (error) {
      if (editingPrompt) {
        setPrompts(prev => prev.map(p => p.id === editingPrompt.id ? { ...p, ...promptData } : p));
      } else {
        const newPrompt = { ...promptData, id: `local_${Date.now()}` };
        setPrompts(prev => [newPrompt, ...prev]);
      }
    }

    setPromptForm({ title: '', content: '', category: 'general' });
    setEditingPrompt(null);
    setShowForm(false);
  };

  const handleDeletePrompt = async (id) => {
    try {
      await supabase.from('prompt_vault').delete().eq('id', id);
    } catch (error) {}
    setPrompts(prev => prev.filter(p => p.id !== id));
  };

  const copyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedId(prompt.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {}
  };

  const sendPromptToChat = (prompt) => {
    sessionStorage.setItem('sage_pending_prompt', prompt.content);
    navigate('chat');
  };

  const filteredPrompts = prompts.filter(p => 
    p.title.toLowerCase().includes(promptSearch.toLowerCase()) ||
    p.content.toLowerCase().includes(promptSearch.toLowerCase())
  );

  // Notes Functions
  const fetchNotes = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      setNotes(data || []);
    } catch (error) {}
    setLoading(false);
  };

  // Voice Dictation for Notes
  const startNoteDictation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try different mimeTypes for browser compatibility
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/mp4';
        if (!MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeForNote(audioBlob);
      };

      // Request data every second for more reliable recording
      mediaRecorderRef.current.start(1000);
      setIsRecordingNote(true);
    } catch (error) {
      console.error('Error starting dictation:', error);
      alert('Could not access microphone. Please allow microphone permissions.');
    }
  };

  const stopNoteDictation = () => {
    if (mediaRecorderRef.current && isRecordingNote) {
      mediaRecorderRef.current.stop();
      setIsRecordingNote(false);
    }
  };

  const transcribeForNote = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await fetch(`${backendUrl}/api/voice/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success && data.text) {
        // Append transcribed text to note content
        setNoteForm(prev => ({
          ...prev,
          content: prev.content ? `${prev.content} ${data.text}` : data.text
        }));
      }
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Tasks Functions
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('due_date', { ascending: true });
      setTasks(data || []);
    } catch (error) {}
    setLoading(false);
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!noteForm.title.trim()) return;

    const noteData = {
      user_id: user.id,
      title: noteForm.title.trim(),
      content: noteForm.content.trim(),
      color: noteForm.color,
      pinned: noteForm.pinned,
      tags: noteForm.tags.trim(),
      updated_at: new Date().toISOString()
    };

    try {
      if (editingNote) {
        const { error } = await supabase.from('notes').update({
          title: noteData.title,
          content: noteData.content,
          color: noteData.color,
          pinned: noteData.pinned,
          tags: noteData.tags,
          updated_at: noteData.updated_at
        }).eq('id', editingNote.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('notes').insert(noteData);
        if (error) throw error;
      }
      setNoteForm({ title: '', content: '', color: '#00d4ff', pinned: false, tags: '' });
      setEditingNote(null);
      setShowForm(false);
      fetchNotes();
    } catch (error) {
      // If DB columns don't exist, save without enhanced fields
      console.log('Note save error (enhanced columns may not exist):', error);
      try {
        if (editingNote) {
          await supabase.from('notes').update({
            title: noteForm.title,
            content: noteForm.content,
            updated_at: new Date().toISOString()
          }).eq('id', editingNote.id);
        } else {
          await supabase.from('notes').insert({
            user_id: user.id,
            title: noteForm.title,
            content: noteForm.content,
            updated_at: new Date().toISOString()
          });
        }
        setNoteForm({ title: '', content: '', color: '#00d4ff', pinned: false, tags: '' });
        setEditingNote(null);
        setShowForm(false);
        fetchNotes();
      } catch (fallbackError) {}
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await supabase.from('notes').delete().eq('id', id);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (error) {}
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;

    const taskData = {
      user_id: user.id,
      title: taskForm.title.trim(),
      description: taskForm.description.trim(),
      due_date: taskForm.due_date || null,
      due_time: taskForm.due_time || null,
      priority: taskForm.priority,
      category: taskForm.category,
      reminder: taskForm.reminder,
      completed: false,
      created_at: new Date().toISOString()
    };

    try {
      const { error } = await supabase.from('tasks').insert(taskData);
      if (error) throw error;
      setTaskForm({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'personal', reminder: false });
      setShowForm(false);
      fetchTasks();
    } catch (error) {
      // If DB columns don't exist, save without enhanced fields
      console.log('Task save error (enhanced columns may not exist):', error);
      try {
        await supabase.from('tasks').insert({
          user_id: user.id,
          title: taskForm.title,
          due_date: taskForm.due_date || null,
          completed: false
        });
        setTaskForm({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'personal', reminder: false });
        setShowForm(false);
        fetchTasks();
      } catch (fallbackError) {}
    }
  };

  const toggleTask = async (id, completed) => {
    try {
      await supabase.from('tasks').update({ completed: !completed }).eq('id', id);
      setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !completed } : t));
    } catch (error) {}
  };

  const handleDeleteTask = async (id) => {
    try {
      await supabase.from('tasks').delete().eq('id', id);
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (error) {}
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const getTasksForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(t => t.due_date === dateStr);
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Tool Grid View
  if (!activeTool) {
    return (
      <div className="min-h-screen bg-[#08080c] pb-20 p-4" data-testid="tools-screen">
        <h2 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em] mb-4">Tools</h2>
        
        <div className="grid grid-cols-2 gap-3" data-testid="tools-grid">
          {tools.map(tool => (
            <button
              key={tool.id}
              onClick={() => { setActiveTool(tool.id); setShowForm(false); }}
              className="bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg text-left hover:border-[#00d4ff] hover:shadow-[0_0_15px_rgba(0,212,255,0.15)] transition-all group"
              data-testid={`tool-${tool.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-[#00d4ff]/10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center group-hover:bg-[#00d4ff]/20 transition-colors">
                  <tool.icon className="w-5 h-5 text-[#00d4ff]" />
                </div>
                {tool.count !== null && (
                  <span className="text-lg font-light text-[#e8e8f0]">{tool.count}</span>
                )}
              </div>
              <h3 className="font-mono text-xs text-[#e8e8f0] uppercase tracking-wider mb-1">{tool.label}</h3>
              <p className="text-[10px] text-[#4a4a60]">{tool.description}</p>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="my-8 flex items-center gap-4">
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[#00d4ff]/50 to-transparent" />
          <span className="font-mono text-[10px] text-[#ff8c00] uppercase tracking-[0.2em]">Future Features</span>
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[#00d4ff]/50 to-transparent" />
        </div>

        {/* Future Features Grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="future-features-grid">
          {/* Code Wizard */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <Code className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">Code Wizard</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              AI code agent to build web & mobile apps. Walk through setup, preview window, deploy to GitHub & more. Ideas to live apps in minutes!
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>

          {/* Family Budget */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">Family Budget</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              Comprehensive family budgeting tool. Track expenses, set goals, manage shared finances with your household.
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>

          {/* Game Hub */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">Game Hub</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              Full gaming experience with casino games, sports betting, and multiplayer challenges. Play responsibly!
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>

          {/* Care Hub */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">Care Hub</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              Caregiver's complete helper. Medication tracking, scheduling, video conferencing, and care coordination all in one place.
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>

          {/* AI University */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">AI University</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              Learn how to master AI and get the most out of Sage. Interactive tutorials, tips, prompting techniques, and best practices.
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>

          {/* Mentoring & Coaching */}
          <div className="bg-[#0d0d12] border border-[#ff8c00]/20 p-4 rounded-lg opacity-60 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-3 h-3 text-[#ff8c00]/50" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-[#ff8c00]/10 border border-[#ff8c00]/20 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-[#ff8c00]" />
              </div>
            </div>
            <h3 className="font-mono text-xs text-[#ff8c00] uppercase tracking-wider mb-1">Mentoring & Coaching</h3>
            <p className="text-[9px] text-[#4a4a60] leading-relaxed">
              Connect with AI coaches and human mentors. Personal development, goal setting, accountability, and growth programs.
            </p>
            <span className="inline-block mt-2 text-[8px] text-[#ff8c00]/60 font-mono uppercase">Coming Soon</span>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center opacity-50 hover:opacity-70 transition-opacity">
          <img src="/owl-logo.png" alt="Sage" className="w-12 h-12 mb-2" style={{ filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.4))' }} />
          <span className="font-mono text-[10px] text-[#00d4ff] tracking-[0.3em]">SAGE</span>
        </div>
      </div>
    );
  }

  // Tool Content Views
  return (
    <div className="min-h-screen bg-[#08080c] pb-20" data-testid="tools-screen">
      {/* Header */}
      <div className="p-4 border-b border-[#1a1a24] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => { setActiveTool(null); setShowForm(false); }} className="p-2 border border-[#1a1a24] hover:border-[#00d4ff]/30 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 text-[#4a4a60]" />
          </button>
          <h1 className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">
            {tools.find(t => t.id === activeTool)?.label}
          </h1>
        </div>
        {!['calendar', 'train-ai', 'support', 'feature-ideas', 'my-submissions'].includes(activeTool) && (
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingNote(null);
              setEditingPrompt(null);
              setEditingGroup(null);
              setNoteForm({ title: '', content: '', color: '#00d4ff', pinned: false, tags: '' });
              setTaskForm({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'personal', reminder: false });
              setEventForm({ title: '', description: '', date: '', start_time: '', end_time: '', location: '', color: '#00d4ff', recurring: 'none', reminder: '15' });
              setPromptForm({ title: '', content: '', category: 'general' });
              setGroupForm({ name: '', type: 'group', description: '' });
            }}
            className="p-2 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-colors"
            data-testid="add-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading && activeTool !== 'train-ai' ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#00d4ff] font-mono text-xs animate-pulse">Loading...</div>
        </div>
      ) : (
        <div className="p-4">
          {/* Notes */}
          {activeTool === 'notes' && (
            <>
              {showForm && (
                <form onSubmit={handleSaveNote} className="mb-4 bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="note-form">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-[#00d4ff]" />
                    <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">{editingNote ? 'Edit' : 'New'} Note</span>
                  </div>
                  
                  {/* Title */}
                  <input 
                    type="text" 
                    value={noteForm.title} 
                    onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })} 
                    placeholder="Note title" 
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-sm font-medium focus:border-[#00d4ff]/50 outline-none mb-3" 
                    data-testid="note-title-input" 
                  />
                  
                  {/* Content with dictation */}
                  <div className="relative mb-3">
                    <textarea 
                      value={noteForm.content} 
                      onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })} 
                      placeholder="Start writing your note... or tap the microphone to dictate" 
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 pr-12 text-xs resize-none focus:border-[#00d4ff]/50 outline-none" 
                      rows={6} 
                      data-testid="note-content-input" 
                    />
                    <button
                      type="button"
                      onClick={isRecordingNote ? stopNoteDictation : startNoteDictation}
                      disabled={isTranscribing}
                      className={`absolute right-2 bottom-2 p-2.5 rounded-full transition-all ${
                        isRecordingNote 
                          ? 'bg-[#ff3366]/20 border-2 border-[#ff3366] animate-pulse' 
                          : 'bg-[#00d4ff]/10 border border-[#00d4ff]/30 hover:border-[#00d4ff] hover:bg-[#00d4ff]/20'
                      } disabled:opacity-50`}
                      data-testid="note-dictate-btn"
                    >
                      {isTranscribing ? (
                        <Loader2 className="w-4 h-4 text-[#00d4ff] animate-spin" />
                      ) : isRecordingNote ? (
                        <MicOff className="w-4 h-4 text-[#ff3366]" />
                      ) : (
                        <Mic className="w-4 h-4 text-[#00d4ff]" />
                      )}
                    </button>
                  </div>
                  
                  {/* Recording Status */}
                  {(isRecordingNote || isTranscribing) && (
                    <div className="flex items-center gap-2 text-[10px] mb-3 p-2 bg-[#08080c] rounded border border-[#1a1a24]">
                      {isRecordingNote && (
                        <>
                          <div className="w-2 h-2 bg-[#ff3366] rounded-full animate-pulse" />
                          <span className="text-[#ff3366] font-mono">Recording... tap microphone to stop</span>
                        </>
                      )}
                      {isTranscribing && (
                        <>
                          <Loader2 className="w-3 h-3 text-[#00d4ff] animate-spin" />
                          <span className="text-[#00d4ff] font-mono">Transcribing your voice...</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Options Row */}
                  <div className="flex items-center gap-3 mb-4">
                    {/* Color Picker */}
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-[#4a4a60] uppercase">Color:</span>
                      <div className="flex gap-1">
                        {noteColors.map(color => (
                          <button
                            key={color.id}
                            type="button"
                            onClick={() => setNoteForm({ ...noteForm, color: color.id })}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${noteForm.color === color.id ? 'border-white scale-110' : 'border-transparent'}`}
                            style={{ backgroundColor: color.id }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Pin Toggle */}
                    <button
                      type="button"
                      onClick={() => setNoteForm({ ...noteForm, pinned: !noteForm.pinned })}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] transition-all ${
                        noteForm.pinned 
                          ? 'bg-[#ffcc00]/20 text-[#ffcc00] border border-[#ffcc00]/50' 
                          : 'bg-[#08080c] text-[#4a4a60] border border-[#1a1a24] hover:border-[#4a4a60]'
                      }`}
                    >
                      <Pin className="w-3 h-3" />
                      {noteForm.pinned ? 'Pinned' : 'Pin'}
                    </button>
                  </div>
                  
                  {/* Tags */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Tag className="w-3 h-3 text-[#4a4a60]" />
                      <span className="text-[9px] text-[#4a4a60] uppercase">Tags (comma separated)</span>
                    </div>
                    <input 
                      type="text" 
                      value={noteForm.tags} 
                      onChange={(e) => setNoteForm({ ...noteForm, tags: e.target.value })} 
                      placeholder="work, ideas, important..." 
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                    />
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2.5 rounded hover:bg-[#00d4ff]/30 flex items-center justify-center gap-2" data-testid="save-note-btn">
                      <Save className="w-3 h-3" />
                      {editingNote ? 'Update' : 'Save'} Note
                    </button>
                    <button type="button" onClick={() => { setShowForm(false); setEditingNote(null); }} className="px-4 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2.5 rounded hover:border-[#4a4a60]">Cancel</button>
                  </div>
                </form>
              )}
              <div className="space-y-2" data-testid="notes-list">
                {notes.length === 0 ? (
                  <div className="text-center py-12"><FileText className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" /><p className="text-[#4a4a60] text-xs font-mono">No notes yet</p></div>
                ) : (
                  notes.map(note => (
                    <div key={note.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-4 hover:border-[#00d4ff]/30 transition-colors" data-testid={`note-${note.id}`} style={{ borderLeftColor: note.color || '#00d4ff', borderLeftWidth: '3px' }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {note.pinned && <Pin className="w-3 h-3 text-[#ffcc00]" />}
                            <h3 className="font-mono text-xs text-[#e8e8f0]">{note.title}</h3>
                          </div>
                          <p className="text-[11px] text-[#7a7a9a] whitespace-pre-wrap">{note.content}</p>
                          {note.tags && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {note.tags.split(',').map((tag, i) => tag.trim() && (
                                <span key={i} className="text-[8px] px-1.5 py-0.5 bg-[#00d4ff]/10 text-[#00d4ff] rounded">{tag.trim()}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-[9px] text-[#4a4a60] mt-2">{new Date(note.updated_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => { setEditingNote(note); setNoteForm({ title: note.title, content: note.content, color: note.color || '#00d4ff', pinned: note.pinned || false, tags: note.tags || '' }); setShowForm(true); }} className="p-2 hover:bg-[#111118] rounded"><Edit2 className="w-3 h-3 text-[#4a4a60]" /></button>
                          <button onClick={() => handleDeleteNote(note.id)} className="p-2 hover:bg-[#111118] rounded"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Tasks */}
          {activeTool === 'tasks' && (
            <>
              {showForm && (
                <form onSubmit={handleSaveTask} className="mb-4 bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="task-form">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckSquare className="w-4 h-4 text-[#00d4ff]" />
                    <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">New Task</span>
                  </div>
                  
                  {/* Title */}
                  <input 
                    type="text" 
                    value={taskForm.title} 
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} 
                    placeholder="Task title" 
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-sm font-medium focus:border-[#00d4ff]/50 outline-none mb-3" 
                    data-testid="task-title-input" 
                  />
                  
                  {/* Description */}
                  <textarea 
                    value={taskForm.description} 
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} 
                    placeholder="Description (optional)" 
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-xs resize-none focus:border-[#00d4ff]/50 outline-none mb-3" 
                    rows={2} 
                    data-testid="task-desc-input" 
                  />
                  
                  {/* Priority Selection */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Flag className="w-3 h-3 text-[#4a4a60]" />
                      <span className="text-[9px] text-[#4a4a60] uppercase">Priority</span>
                    </div>
                    <div className="flex gap-2">
                      {taskPriorities.map(priority => (
                        <button
                          key={priority.id}
                          type="button"
                          onClick={() => setTaskForm({ ...taskForm, priority: priority.id })}
                          className={`flex-1 py-2 px-3 rounded text-[10px] font-mono uppercase transition-all border ${
                            taskForm.priority === priority.id 
                              ? 'border-2' 
                              : 'border-[#1a1a24] hover:border-[#4a4a60]'
                          }`}
                          style={{ 
                            borderColor: taskForm.priority === priority.id ? priority.color : undefined,
                            backgroundColor: taskForm.priority === priority.id ? `${priority.color}15` : '#08080c',
                            color: taskForm.priority === priority.id ? priority.color : '#7a7a9a'
                          }}
                        >
                          {priority.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Category Selection */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-3 h-3 text-[#4a4a60]" />
                      <span className="text-[9px] text-[#4a4a60] uppercase">Category</span>
                    </div>
                    <select 
                      value={taskForm.category} 
                      onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })} 
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none focus:border-[#00d4ff]/50"
                      data-testid="task-category-select"
                    >
                      {taskCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                  </div>
                  
                  {/* Due Date & Time */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarIcon className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">Due Date</span>
                      </div>
                      <input 
                        type="date" 
                        value={taskForm.due_date} 
                        onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                        data-testid="task-date-input" 
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">Due Time</span>
                      </div>
                      <input 
                        type="time" 
                        value={taskForm.due_time} 
                        onChange={(e) => setTaskForm({ ...taskForm, due_time: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                        data-testid="task-time-input" 
                      />
                    </div>
                  </div>
                  
                  {/* Reminder Toggle */}
                  <div className="flex items-center justify-between mb-4 p-3 bg-[#08080c] border border-[#1a1a24] rounded">
                    <div className="flex items-center gap-2">
                      {taskForm.reminder ? <Bell className="w-4 h-4 text-[#00d4ff]" /> : <BellOff className="w-4 h-4 text-[#4a4a60]" />}
                      <span className="text-xs text-[#e8e8f0]">Reminder</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTaskForm({ ...taskForm, reminder: !taskForm.reminder })}
                      className={`w-12 h-6 rounded-full transition-all relative ${taskForm.reminder ? 'bg-[#00d4ff]/30' : 'bg-[#1a1a24]'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full transition-all ${taskForm.reminder ? 'left-7 bg-[#00d4ff]' : 'left-1 bg-[#4a4a60]'}`} />
                    </button>
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2.5 rounded hover:bg-[#00d4ff]/30 flex items-center justify-center gap-2" data-testid="save-task-btn">
                      <Save className="w-3 h-3" />
                      Add Task
                    </button>
                    <button type="button" onClick={() => setShowForm(false)} className="px-4 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2.5 rounded hover:border-[#4a4a60]">Cancel</button>
                  </div>
                </form>
              )}
              <div className="space-y-2" data-testid="tasks-list">
                {tasks.length === 0 ? (
                  <div className="text-center py-12"><CheckSquare className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" /><p className="text-[#4a4a60] text-xs font-mono">No tasks yet</p></div>
                ) : (
                  tasks.map(task => {
                    const priorityInfo = taskPriorities.find(p => p.id === task.priority) || taskPriorities[1];
                    const categoryInfo = taskCategories.find(c => c.id === task.category);
                    return (
                      <div key={task.id} className={`bg-[#0d0d12] border rounded-lg p-3 ${task.completed ? 'opacity-50 border-[#1a1a24]' : 'border-l-2'}`} style={{ borderLeftColor: task.completed ? undefined : priorityInfo.color }}>
                        <div className="flex items-start gap-3">
                          <button onClick={() => toggleTask(task.id, task.completed)} className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 flex-shrink-0 ${task.completed ? 'bg-[#00ff88]/20 border-[#00ff88]/50 text-[#00ff88]' : 'border-[#1a1a24] hover:border-[#00d4ff]'}`}>{task.completed && <Check className="w-3 h-3" />}</button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <p className={`text-xs font-medium ${task.completed ? 'line-through text-[#4a4a60]' : 'text-[#e8e8f0]'}`}>{task.title}</p>
                              {task.priority && task.priority !== 'medium' && (
                                <span className="text-[8px] px-1.5 py-0.5 rounded uppercase font-mono" style={{ backgroundColor: `${priorityInfo.color}15`, color: priorityInfo.color }}>{task.priority}</span>
                              )}
                              {categoryInfo && (
                                <span className="text-[8px] px-1.5 py-0.5 bg-[#00d4ff]/10 text-[#00d4ff] rounded">{categoryInfo.name}</span>
                              )}
                            </div>
                            {task.description && <p className="text-[10px] text-[#7a7a9a] mb-1">{task.description}</p>}
                            <div className="flex items-center gap-3 text-[9px] text-[#4a4a60]">
                              {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}{task.due_time ? ` at ${task.due_time}` : ''}</span>}
                              {task.reminder && <Bell className="w-3 h-3 text-[#00d4ff]" />}
                            </div>
                          </div>
                          <button onClick={() => handleDeleteTask(task.id)} className="p-2 hover:bg-[#111118] rounded flex-shrink-0"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Prompts */}
          {activeTool === 'prompts' && (
            <>
              {showForm && (
                <form onSubmit={handleSavePrompt} className="mb-4 space-y-3 bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="prompt-form">
                  <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#00d4ff]" /><span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">{editingPrompt ? 'Edit' : 'New'} Prompt</span></div>
                  <input type="text" value={promptForm.title} onChange={(e) => setPromptForm({ ...promptForm, title: e.target.value })} placeholder="Prompt name" className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" />
                  <select value={promptForm.category} onChange={(e) => setPromptForm({ ...promptForm, category: e.target.value })} className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none">
                    {promptCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                  <textarea value={promptForm.content} onChange={(e) => setPromptForm({ ...promptForm, content: e.target.value })} placeholder="Enter your prompt..." className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs resize-none outline-none" rows={5} />
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 rounded">{editingPrompt ? 'Update' : 'Save'}</button>
                    <button type="button" onClick={() => { setShowForm(false); setEditingPrompt(null); }} className="px-4 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 rounded">Cancel</button>
                  </div>
                </form>
              )}
              {!showForm && (
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a60]" />
                  <input type="text" value={promptSearch} onChange={(e) => setPromptSearch(e.target.value)} placeholder="Search prompts..." className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] rounded pl-10 pr-3 py-2 text-xs outline-none" />
                </div>
              )}
              <div className="space-y-2">
                {filteredPrompts.length === 0 ? (
                  <div className="text-center py-12"><Sparkles className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" /><p className="text-[#4a4a60] text-xs font-mono">No prompts yet</p></div>
                ) : (
                  filteredPrompts.map(prompt => {
                    const category = promptCategories.find(c => c.id === prompt.category) || promptCategories[0];
                    return (
                      <div key={prompt.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-4 hover:border-[#00d4ff]/30 transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-mono text-xs text-[#e8e8f0]">{prompt.title}</h3>
                            <span className="text-[8px] px-1.5 py-0.5 rounded uppercase font-mono" style={{ backgroundColor: `${category.color}20`, color: category.color }}>{category.name}</span>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => copyPrompt(prompt)} className={`p-2 hover:bg-[#111118] rounded ${copiedId === prompt.id ? 'text-[#00ff88]' : 'text-[#4a4a60]'}`}>{copiedId === prompt.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}</button>
                            <button onClick={() => { setEditingPrompt(prompt); setPromptForm({ title: prompt.title, content: prompt.content, category: prompt.category }); setShowForm(true); }} className="p-2 hover:bg-[#111118] rounded"><Edit2 className="w-3 h-3 text-[#4a4a60]" /></button>
                            <button onClick={() => handleDeletePrompt(prompt.id)} className="p-2 hover:bg-[#111118] rounded"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                          </div>
                        </div>
                        <p className="text-[11px] text-[#7a7a9a] whitespace-pre-wrap line-clamp-3 mb-3">{prompt.content}</p>
                        <button onClick={() => sendPromptToChat(prompt)} className="w-full border border-[#00d4ff]/30 text-[#00d4ff] font-mono text-[9px] uppercase py-2 rounded hover:bg-[#00d4ff]/10">Use in Chat</button>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Calendar */}
          {activeTool === 'calendar' && (
            <div data-testid="calendar-view">
              {/* Event Creation Form */}
              {showForm && (
                <form onSubmit={handleSaveEvent} className="mb-4 bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="event-form">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarIcon className="w-4 h-4 text-[#00d4ff]" />
                    <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">{editingEvent ? 'Edit' : 'New'} Event</span>
                  </div>
                  
                  {/* Title */}
                  <input 
                    type="text" 
                    value={eventForm.title} 
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} 
                    placeholder="Event title" 
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-sm font-medium focus:border-[#00d4ff]/50 outline-none mb-3" 
                    data-testid="event-title-input" 
                  />
                  
                  {/* Description */}
                  <textarea 
                    value={eventForm.description} 
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} 
                    placeholder="Description (optional)" 
                    className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-xs resize-none focus:border-[#00d4ff]/50 outline-none mb-3" 
                    rows={2} 
                    data-testid="event-desc-input" 
                  />
                  
                  {/* Date */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarIcon className="w-3 h-3 text-[#4a4a60]" />
                      <span className="text-[9px] text-[#4a4a60] uppercase">Date</span>
                    </div>
                    <input 
                      type="date" 
                      value={eventForm.date} 
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} 
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                      data-testid="event-date-input" 
                    />
                  </div>
                  
                  {/* Time Range */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">Start Time</span>
                      </div>
                      <input 
                        type="time" 
                        value={eventForm.start_time} 
                        onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                        data-testid="event-start-time" 
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">End Time</span>
                      </div>
                      <input 
                        type="time" 
                        value={eventForm.end_time} 
                        onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                        data-testid="event-end-time" 
                      />
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-3 h-3 text-[#4a4a60]" />
                      <span className="text-[9px] text-[#4a4a60] uppercase">Location</span>
                    </div>
                    <input 
                      type="text" 
                      value={eventForm.location} 
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} 
                      placeholder="Add location (optional)" 
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" 
                      data-testid="event-location-input" 
                    />
                  </div>
                  
                  {/* Color Picker */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[9px] text-[#4a4a60] uppercase">Color:</span>
                    <div className="flex gap-1">
                      {noteColors.map(color => (
                        <button
                          key={color.id}
                          type="button"
                          onClick={() => setEventForm({ ...eventForm, color: color.id })}
                          className={`w-5 h-5 rounded-full border-2 transition-all ${eventForm.color === color.id ? 'border-white scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: color.id }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Recurring & Reminder */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Repeat className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">Repeat</span>
                      </div>
                      <select 
                        value={eventForm.recurring} 
                        onChange={(e) => setEventForm({ ...eventForm, recurring: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none focus:border-[#00d4ff]/50"
                      >
                        {recurringOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className="w-3 h-3 text-[#4a4a60]" />
                        <span className="text-[9px] text-[#4a4a60] uppercase">Reminder</span>
                      </div>
                      <select 
                        value={eventForm.reminder} 
                        onChange={(e) => setEventForm({ ...eventForm, reminder: e.target.value })} 
                        className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none focus:border-[#00d4ff]/50"
                      >
                        {reminderOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                      </select>
                    </div>
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2.5 rounded hover:bg-[#00d4ff]/30 flex items-center justify-center gap-2" data-testid="save-event-btn">
                      <Save className="w-3 h-3" />
                      {editingEvent ? 'Update' : 'Create'} Event
                    </button>
                    <button type="button" onClick={() => { setShowForm(false); setEditingEvent(null); setSelectedDay(null); }} className="px-4 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2.5 rounded hover:border-[#4a4a60]">Cancel</button>
                  </div>
                </form>
              )}

              {/* Calendar Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 border border-[#1a1a24] hover:border-[#00d4ff]/30 text-[#4a4a60] rounded" data-testid="prev-month-btn">←</button>
                <div className="flex items-center gap-3">
                  <h2 className="font-mono text-xs text-[#e8e8f0]">{monthName}</h2>
                  <button 
                    onClick={() => {
                      setShowForm(true);
                      setEditingEvent(null);
                      setEventForm({ title: '', description: '', date: '', start_time: '', end_time: '', location: '', color: '#00d4ff', recurring: 'none', reminder: '15' });
                    }}
                    className="p-1.5 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-colors rounded"
                    data-testid="add-event-btn"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 border border-[#1a1a24] hover:border-[#00d4ff]/30 text-[#4a4a60] rounded" data-testid="next-month-btn">→</button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="text-center font-mono text-[9px] text-[#4a4a60] uppercase py-2">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} className="aspect-square" />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayTasks = getTasksForDay(day);
                  const dayEvents = getEventsForDay(day);
                  const today = new Date();
                  const isToday = day === today.getDate() && currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
                  const isSelected = selectedDay === day;
                  return (
                    <button
                      key={day}
                      onClick={() => {
                        setSelectedDay(day);
                        const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        setEventForm(prev => ({ ...prev, date: dateStr }));
                      }}
                      className={`aspect-square bg-[#0d0d12] border rounded p-1 flex flex-col transition-all ${
                        isSelected ? 'border-[#00ff88] bg-[#00ff88]/10' : 
                        isToday ? 'border-[#00d4ff]' : 'border-[#1a1a24] hover:border-[#00d4ff]/30'
                      }`}
                    >
                      <span className={`text-[9px] font-mono ${isSelected ? 'text-[#00ff88]' : isToday ? 'text-[#00d4ff]' : 'text-[#4a4a60]'}`}>{day}</span>
                      <div className="flex-1 flex items-end gap-0.5">
                        {dayTasks.length > 0 && <div className="flex-1 h-1 bg-[#00d4ff] rounded-full" />}
                        {dayEvents.length > 0 && <div className="flex-1 h-1 bg-[#00ff88] rounded-full" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 mb-4">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-[#00d4ff] rounded-full" />
                  <span className="text-[9px] text-[#4a4a60]">Tasks</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-[#00ff88] rounded-full" />
                  <span className="text-[9px] text-[#4a4a60]">Events</span>
                </div>
              </div>
              
              {/* Selected Day Events/Tasks */}
              {selectedDay && (
                <div className="mb-4 p-3 bg-[#0d0d12] border border-[#00ff88]/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-mono text-[10px] text-[#00ff88] uppercase tracking-wider">
                      {new Date(currentMonth.getFullYear(), currentMonth.getMonth(), selectedDay).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </h3>
                    <button
                      onClick={() => {
                        setShowForm(true);
                        setEditingEvent(null);
                      }}
                      className="text-[9px] text-[#00d4ff] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Event
                    </button>
                  </div>
                  {getEventsForDay(selectedDay).length === 0 && getTasksForDay(selectedDay).length === 0 ? (
                    <p className="text-[11px] text-[#4a4a60]">No events or tasks scheduled</p>
                  ) : (
                    <div className="space-y-2">
                      {getEventsForDay(selectedDay).map(event => (
                        <div key={event.id} className="bg-[#08080c] border rounded p-2 flex items-center gap-2" style={{ borderColor: `${event.color}50` }}>
                          <div className="w-2 h-full rounded-full" style={{ backgroundColor: event.color }} />
                          <div className="flex-1">
                            <p className="text-xs text-[#e8e8f0]">{event.title}</p>
                            {event.start_time && <p className="text-[9px] text-[#4a4a60]">{event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}</p>}
                            {event.location && <p className="text-[9px] text-[#4a4a60] flex items-center gap-1"><MapPin className="w-2 h-2" />{event.location}</p>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditingEvent(event); setEventForm(event); setShowForm(true); }} className="p-1.5 hover:bg-[#111118] rounded"><Edit2 className="w-3 h-3 text-[#4a4a60]" /></button>
                            <button onClick={() => handleDeleteEvent(event.id)} className="p-1.5 hover:bg-[#111118] rounded"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                          </div>
                        </div>
                      ))}
                      {getTasksForDay(selectedDay).map(task => (
                        <div key={task.id} className="bg-[#08080c] border border-[#00d4ff]/30 rounded p-2 flex items-center gap-2">
                          <CheckSquare className="w-3 h-3 text-[#00d4ff]" />
                          <span className={`text-xs flex-1 ${task.completed ? 'line-through text-[#4a4a60]' : 'text-[#e8e8f0]'}`}>{task.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Upcoming Events */}
              <div className="mt-4">
                <h3 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-2">Upcoming Events</h3>
                <div className="space-y-2">
                  {events.filter(e => new Date(e.date) >= new Date(new Date().toDateString())).slice(0, 5).length === 0 ? (
                    <div className="text-center py-4 bg-[#0d0d12] border border-[#1a1a24] rounded">
                      <CalendarIcon className="w-6 h-6 text-[#2a2a3a] mx-auto mb-2" />
                      <p className="text-[10px] text-[#4a4a60]">No upcoming events</p>
                    </div>
                  ) : (
                    events.filter(e => new Date(e.date) >= new Date(new Date().toDateString())).slice(0, 5).map(event => (
                      <div key={event.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded p-3 flex items-center gap-3 hover:border-[#00d4ff]/30 transition-colors">
                        <div className="w-1 h-10 rounded-full" style={{ backgroundColor: event.color }} />
                        <div className="flex-1">
                          <p className="text-xs text-[#e8e8f0] font-medium">{event.title}</p>
                          <p className="text-[10px] text-[#4a4a60]">
                            {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            {event.start_time && ` at ${event.start_time}`}
                          </p>
                          {event.location && <p className="text-[9px] text-[#00d4ff] flex items-center gap-1"><MapPin className="w-2 h-2" />{event.location}</p>}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => { setEditingEvent(event); setEventForm(event); setShowForm(true); }} className="p-2 hover:bg-[#111118] rounded"><Edit2 className="w-3 h-3 text-[#4a4a60]" /></button>
                          <button onClick={() => handleDeleteEvent(event.id)} className="p-2 hover:bg-[#111118] rounded"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Tasks This Month */}
              <div className="mt-4">
                <h3 className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-2">Tasks This Month</h3>
                <div className="space-y-2">
                  {tasks.filter(t => { if (!t.due_date) return false; const d = new Date(t.due_date); return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear(); }).length === 0 ? (
                    <div className="text-center py-4 bg-[#0d0d12] border border-[#1a1a24] rounded">
                      <CheckSquare className="w-6 h-6 text-[#2a2a3a] mx-auto mb-2" />
                      <p className="text-[10px] text-[#4a4a60]">No tasks this month</p>
                    </div>
                  ) : (
                    tasks.filter(t => { if (!t.due_date) return false; const d = new Date(t.due_date); return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear(); }).map(task => (
                      <div key={task.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded p-2 flex items-center gap-2">
                        <Clock className="w-3 h-3 text-[#00d4ff]" />
                        <span className={`text-xs flex-1 ${task.completed ? 'line-through text-[#4a4a60]' : 'text-[#e8e8f0]'}`}>{task.title}</span>
                        <span className="text-[9px] text-[#4a4a60]">{new Date(task.due_date).toLocaleDateString()}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Friends Groups */}
          {activeTool === 'friends' && (
            <>
              {showForm && (
                <form onSubmit={handleSaveGroup} className="mb-4 space-y-3 bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="group-form">
                  <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-[#00d4ff]" /><span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">{editingGroup ? 'Edit' : 'New'} Group</span></div>
                  <input type="text" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="Group name" className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" data-testid="group-name-input" />
                  <select value={groupForm.type} onChange={(e) => setGroupForm({ ...groupForm, type: e.target.value })} className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none" data-testid="group-type-select">
                    {groupTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                  </select>
                  <textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} placeholder="Description (optional)" className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs resize-none outline-none" rows={2} data-testid="group-desc-input" />
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 rounded" data-testid="save-group-btn">{editingGroup ? 'Update' : 'Create'} Group</button>
                    <button type="button" onClick={() => { setShowForm(false); setEditingGroup(null); }} className="px-4 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 rounded">Cancel</button>
                  </div>
                </form>
              )}
              <div className="space-y-2" data-testid="groups-list">
                {friendGroups.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" />
                    <p className="text-[#4a4a60] text-xs font-mono">No groups yet</p>
                    <p className="text-[#3a3a4a] text-[10px] mt-1">Create groups to organize your friends</p>
                  </div>
                ) : (
                  friendGroups.map(group => {
                    const typeInfo = groupTypes.find(t => t.id === group.type) || groupTypes[0];
                    const memberCount = group.friend_group_members?.length || 0;
                    return (
                      <div key={group.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-4 hover:border-[#00d4ff]/30 transition-colors" data-testid={`group-${group.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-mono text-xs text-[#e8e8f0]">{group.name}</h3>
                            <span className="text-[8px] px-1.5 py-0.5 rounded uppercase font-mono" style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}>{typeInfo.name}</span>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditingGroup(group); setGroupForm({ name: group.name, type: group.type, description: group.description || '' }); setShowForm(true); }} className="p-2 hover:bg-[#111118] rounded"><Edit2 className="w-3 h-3 text-[#4a4a60]" /></button>
                            <button onClick={() => handleDeleteGroup(group.id)} className="p-2 hover:bg-[#111118] rounded"><Trash2 className="w-3 h-3 text-[#4a4a60]" /></button>
                          </div>
                        </div>
                        {group.description && <p className="text-[11px] text-[#7a7a9a] mb-2">{group.description}</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-[#4a4a60]">{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                          <button className="text-[10px] text-[#00d4ff] hover:underline flex items-center gap-1">
                            <UserPlus className="w-3 h-3" /> Add Members
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Train My AI */}
          {activeTool === 'train-ai' && (
            <form onSubmit={handleSaveAiPreferences} className="space-y-4" data-testid="train-ai-form">
              {/* Success Banner */}
              {saveSuccess && (
                <div className="bg-[#00ff88]/10 border border-[#00ff88]/30 p-3 rounded-lg flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00ff88]" />
                  <span className="font-mono text-xs text-[#00ff88]">Preferences saved!</span>
                </div>
              )}

              <div className="bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-4 h-4 text-[#00d4ff]" />
                  <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">Personalize Your AI</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Your Name</label>
                    <input type="text" value={aiForm.user_name} onChange={(e) => setAiForm({ ...aiForm, user_name: e.target.value })} placeholder="What should Sage call you?" className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" data-testid="ai-user-name" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">AI Nickname</label>
                    <input type="text" value={aiForm.ai_nickname} onChange={(e) => setAiForm({ ...aiForm, ai_nickname: e.target.value })} placeholder="What do you call your AI? (default: Sage)" className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" data-testid="ai-nickname" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Communication Style</label>
                    <select value={aiForm.communication_style} onChange={(e) => setAiForm({ ...aiForm, communication_style: e.target.value })} className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs outline-none" data-testid="ai-comm-style">
                      {communicationStyles.map(style => <option key={style.id} value={style.id}>{style.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">About You</label>
                    <textarea value={aiForm.about_me} onChange={(e) => setAiForm({ ...aiForm, about_me: e.target.value })} placeholder="Tell Sage about yourself..." className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs resize-none focus:border-[#00d4ff]/50 outline-none" rows={3} data-testid="ai-about-me" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Your Interests</label>
                    <input type="text" value={aiForm.interests} onChange={(e) => setAiForm({ ...aiForm, interests: e.target.value })} placeholder="e.g., technology, music, fitness, reading..." className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" data-testid="ai-interests" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Work Context</label>
                    <input type="text" value={aiForm.work_context} onChange={(e) => setAiForm({ ...aiForm, work_context: e.target.value })} placeholder="e.g., Software developer, Business owner, Student..." className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs focus:border-[#00d4ff]/50 outline-none" data-testid="ai-work" />
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Custom Instructions</label>
                    <textarea value={aiForm.custom_instructions} onChange={(e) => setAiForm({ ...aiForm, custom_instructions: e.target.value })} placeholder="Any specific instructions for how Sage should respond to you..." className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs resize-none focus:border-[#00d4ff]/50 outline-none" rows={3} data-testid="ai-instructions" />
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-3 rounded-lg hover:bg-[#00d4ff]/30 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-ai-prefs-btn">
                <Save className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>

              <p className="text-[10px] text-[#4a4a60] text-center">
                These preferences help Sage understand you better and provide more personalized responses.
              </p>
            </form>
          )}

          {/* Support */}
          {activeTool === 'support' && (
            <div data-testid="support-view">
              {submitSuccess === 'support' && (
                <div className="bg-[#00ff88]/10 border border-[#00ff88]/30 p-3 rounded-lg flex items-center gap-2 mb-4">
                  <Check className="w-4 h-4 text-[#00ff88]" />
                  <span className="font-mono text-xs text-[#00ff88]">Support request submitted!</span>
                </div>
              )}
              
              <form onSubmit={handleSubmitSupport} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="support-form">
                <div className="flex items-center gap-2 mb-4">
                  <HelpCircle className="w-4 h-4 text-[#00d4ff]" />
                  <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">Report an Issue</span>
                </div>
                
                <input 
                  type="text" 
                  value={supportForm.subject} 
                  onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })} 
                  placeholder="Subject - What's the issue?" 
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-sm font-medium focus:border-[#00d4ff]/50 outline-none mb-3" 
                  data-testid="support-subject" 
                />
                
                <textarea 
                  value={supportForm.description} 
                  onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })} 
                  placeholder="Describe the issue in detail. Include steps to reproduce if possible..." 
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-xs resize-none focus:border-[#00d4ff]/50 outline-none mb-3" 
                  rows={5} 
                  data-testid="support-description" 
                />
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-3 h-3 text-[#4a4a60]" />
                    <span className="text-[9px] text-[#4a4a60] uppercase">Priority</span>
                  </div>
                  <div className="flex gap-2">
                    {supportPriorities.map(priority => (
                      <button
                        key={priority.id}
                        type="button"
                        onClick={() => setSupportForm({ ...supportForm, priority: priority.id })}
                        className={`flex-1 py-2 px-3 rounded text-[10px] font-mono uppercase transition-all border ${
                          supportForm.priority === priority.id ? 'border-2' : 'border-[#1a1a24] hover:border-[#4a4a60]'
                        }`}
                        style={{ 
                          borderColor: supportForm.priority === priority.id ? priority.color : undefined,
                          backgroundColor: supportForm.priority === priority.id ? `${priority.color}15` : '#08080c',
                          color: supportForm.priority === priority.id ? priority.color : '#7a7a9a'
                        }}
                      >
                        {priority.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button type="submit" className="w-full bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2.5 rounded hover:bg-[#00d4ff]/30 flex items-center justify-center gap-2" data-testid="submit-support-btn">
                  <Send className="w-3 h-3" />
                  Submit Support Request
                </button>
              </form>
              
              <p className="text-[10px] text-[#4a4a60] text-center mt-4">
                Your request will be sent to the admin team. Check "My Submissions" to track status.
              </p>
            </div>
          )}

          {/* Feature Ideas */}
          {activeTool === 'feature-ideas' && (
            <div data-testid="feature-ideas-view">
              {submitSuccess === 'feature' && (
                <div className="bg-[#00ff88]/10 border border-[#00ff88]/30 p-3 rounded-lg flex items-center gap-2 mb-4">
                  <Check className="w-4 h-4 text-[#00ff88]" />
                  <span className="font-mono text-xs text-[#00ff88]">Feature suggestion submitted!</span>
                </div>
              )}
              
              <form onSubmit={handleSubmitFeature} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-4 rounded-lg" data-testid="feature-form">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-4 h-4 text-[#ffcc00]" />
                  <span className="font-mono text-[10px] text-[#ffcc00] uppercase tracking-wider">Suggest a Feature</span>
                </div>
                
                <input 
                  type="text" 
                  value={featureForm.title} 
                  onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })} 
                  placeholder="Feature title - What would you like to see?" 
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-sm font-medium focus:border-[#ffcc00]/50 outline-none mb-3" 
                  data-testid="feature-title" 
                />
                
                <textarea 
                  value={featureForm.description} 
                  onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })} 
                  placeholder="Describe your idea in detail. How would it work? Why would it be useful?" 
                  className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2.5 text-xs resize-none focus:border-[#ffcc00]/50 outline-none mb-3" 
                  rows={5} 
                  data-testid="feature-description" 
                />
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-3 h-3 text-[#4a4a60]" />
                    <span className="text-[9px] text-[#4a4a60] uppercase">Category</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {featureCategories.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setFeatureForm({ ...featureForm, category: cat.id })}
                        className={`py-2 px-3 rounded text-[10px] font-mono uppercase transition-all border ${
                          featureForm.category === cat.id ? 'border-2' : 'border-[#1a1a24] hover:border-[#4a4a60]'
                        }`}
                        style={{ 
                          borderColor: featureForm.category === cat.id ? cat.color : undefined,
                          backgroundColor: featureForm.category === cat.id ? `${cat.color}15` : '#08080c',
                          color: featureForm.category === cat.id ? cat.color : '#7a7a9a'
                        }}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button type="submit" className="w-full bg-[#ffcc00]/20 border border-[#ffcc00] text-[#ffcc00] font-mono text-[10px] uppercase py-2.5 rounded hover:bg-[#ffcc00]/30 flex items-center justify-center gap-2" data-testid="submit-feature-btn">
                  <Lightbulb className="w-3 h-3" />
                  Submit Feature Idea
                </button>
              </form>
              
              <p className="text-[10px] text-[#4a4a60] text-center mt-4">
                Great ideas make the app better! Check "My Submissions" to track your suggestions.
              </p>
            </div>
          )}

          {/* My Submissions */}
          {activeTool === 'my-submissions' && (
            <div data-testid="submissions-view">
              {submissions.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" />
                  <p className="text-[#4a4a60] text-xs font-mono">No submissions yet</p>
                  <p className="text-[#3a3a4a] text-[10px] mt-1">Submit support requests or feature ideas to see them here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {submissions.map(sub => {
                    const status = submissionStatuses[sub.status] || submissionStatuses.pending;
                    const isSupport = sub.type === 'support';
                    const priorityInfo = isSupport 
                      ? supportPriorities.find(p => p.id === sub.priority) 
                      : featureCategories.find(c => c.id === sub.priority);
                    
                    return (
                      <div key={sub.id} className="bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-4 hover:border-[#00d4ff]/30 transition-colors" data-testid={`submission-${sub.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {isSupport ? (
                              <HelpCircle className="w-4 h-4 text-[#00d4ff]" />
                            ) : (
                              <Lightbulb className="w-4 h-4 text-[#ffcc00]" />
                            )}
                            <h3 className="font-mono text-xs text-[#e8e8f0]">{sub.subject}</h3>
                          </div>
                          <span 
                            className="text-[8px] px-2 py-1 rounded uppercase font-mono"
                            style={{ backgroundColor: `${status.color}20`, color: status.color }}
                          >
                            {status.label}
                          </span>
                        </div>
                        
                        <p className="text-[11px] text-[#7a7a9a] mb-3 line-clamp-2">{sub.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-[8px] px-1.5 py-0.5 rounded uppercase font-mono" style={{ backgroundColor: `${priorityInfo?.color || '#4a4a60'}20`, color: priorityInfo?.color || '#4a4a60' }}>
                              {isSupport ? sub.priority : priorityInfo?.name || sub.priority}
                            </span>
                            <span className="text-[9px] text-[#4a4a60]">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <span className="text-[8px] text-[#4a4a60] uppercase">
                            {isSupport ? 'Support' : 'Feature'}
                          </span>
                        </div>
                        
                        {sub.admin_response && (
                          <div className="mt-3 p-2 bg-[#08080c] border border-[#00d4ff]/20 rounded">
                            <p className="text-[9px] text-[#00d4ff] uppercase mb-1">Admin Response:</p>
                            <p className="text-[10px] text-[#e8e8f0]">{sub.admin_response}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
